import type { RequestFn } from '../http';

/** Admin → Einstellungen → WhatsApp (Betriebszustand ChatWerk / WhatsApp Cloud API). */
export interface WhatsappAdminStatus {
  provider: 'chatwerk' | 'meta';
  shadow: boolean;
  /** Meta-Events werden auf den Chat-Spiegel angewendet (meta und nicht Shadow). */
  metaApplyActive: boolean;
  webhookUrl: string;
  config: {
    chatwerk: boolean;
    metaSend: boolean;
    metaWebhook: boolean;
    graphApiVersion: string | null;
    phoneNumberId: string | null;
    wabaId: string | null;
    accessToken: boolean;
    appSecret: boolean;
    verifyToken: boolean;
    statusTemplate: string | null;
  };
  lastEvent: { receivedAt: string; kind: string; eventType: string | null; status: string } | null;
  lastRejected: { receivedAt: string; status: string; errorMessage: string | null } | null;
  eventsLast24h: Record<string, number>;
  authAlarm: { at: string; message: string | null; fbtraceId: string | null } | null;
  templates: { total: number; approved: number };
  conversations: {
    withoutWaId: number;
    withoutWaIdBackfillable: number;
    duplicateOpenWaIds: { waId: string; count: number }[];
  };
  chatwerkMedia: {
    pending: number;
    failed: number;
    stored: number;
    archive: WhatsappMediaArchiveProgress;
  };
}

export interface WhatsappMediaArchiveProgress {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  total: number;
  ok: number;
  expired: number;
  error: number;
  lastError: string | null;
}

export interface WhatsappAdminTemplate {
  name: string;
  language: string;
  status: string;
  category: string | null;
  parameterFormat: string;
  bodyText: string;
  placeholders: string[];
  sendable: boolean;
  unsupportedReason: string | null;
  syncedAt: string;
}

export interface WhatsappWebhookEventRow {
  id: string;
  eventKey: string;
  kind: string;
  eventType: string | null;
  status: string;
  shadow: boolean;
  attempts: number;
  errorMessage: string | null;
  occurredAt: string | null;
  receivedAt: string;
  processedAt: string | null;
}

export interface WhatsappShadowItem {
  at: string;
  sender: string | null;
  type: string;
  text: string | null;
}

export interface WhatsappShadowReport {
  from: string;
  to: string;
  metaMessages: number;
  chatwerkMessages: number;
  matched: number;
  metaWithoutPhone: number;
  metaStatuses: number;
  onlyMeta: WhatsappShadowItem[];
  onlyChatwerk: WhatsappShadowItem[];
}

export function createWhatsappAdminApi(request: RequestFn) {
  return {
    getWhatsappAdminStatus() {
      return request<WhatsappAdminStatus>('/admin/whatsapp/status');
    },
    getWhatsappWebhookEvents(status?: string) {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request<{ events: WhatsappWebhookEventRow[] }>(`/admin/whatsapp/events${qs}`);
    },
    getWhatsappAdminTemplates() {
      return request<{ templates: WhatsappAdminTemplate[] }>('/admin/whatsapp/templates');
    },
    syncWhatsappTemplates() {
      return request<{ skipped: boolean; fetched: number; upserted: number; markedDeleted: number }>(
        '/admin/whatsapp/templates/sync',
        { method: 'POST' },
      );
    },
    backfillWhatsappWaIds() {
      return request<{ updated: number; duplicateOpenWaIds: { waId: string; count: number }[] }>(
        '/admin/whatsapp/backfill-wa-ids',
        { method: 'POST' },
      );
    },
    getWhatsappShadowReport(params: { from?: string; to?: string } = {}) {
      const qs = new URLSearchParams();
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const query = qs.toString();
      return request<WhatsappShadowReport>(`/admin/whatsapp/shadow-report${query ? `?${query}` : ''}`);
    },
    startChatwerkMediaArchive(retryFailed = false) {
      return request<{ started: boolean }>('/admin/whatsapp/chatwerk-media-archive', {
        method: 'POST',
        body: JSON.stringify({ retryFailed }),
      });
    },
  };
}
